import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchTrendingTopicsThunk,
  followCommunityAuthorThunk,
  hydrateSavedPosts,
  unfollowCommunityAuthorThunk,
} from '../../redux/communitySlice';
import CommunityLeftNav from '../components/CommunityLeftNav';
import CommunityTopBar from '../components/CommunityTopBar';
import CommunityRightAside from '../components/CommunityRightAside';
import CommunityFloatingComposerButton from '../components/CommunityFloatingComposerButton';
import { useDebounce } from '../hooks/useDebounce';
import { loadSavedPostIds, persistSavedPostIds } from '../../utils/communityStorage';
import { useNotificationStream } from '../../hooks/useNotificationStream';
import './CommunityLayout.css';

type RecommendedCreator = {
  id: number;
  name: string;
  username?: string | null;
  avatar_url?: string | null;
  posts: number;
  likes: number;
  comments: number;
  followsAuthor: boolean;
  topTopics: string[];
  latestTitle?: string;
  latestCreatedAt?: string | null;
};

export type CommunityLayoutOutletContext = {
  searchTerm: string;
  searchInput: string;
  setSearchInput: (value: string) => void;
};

const CommunityLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { trendingTopics, trendingStatus, trendingError, savedPostIds, feedItems } = useAppSelector((state) => state.community);

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, 400);
  const [followPendingMap, setFollowPendingMap] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (trendingStatus === 'idle') {
      dispatch(fetchTrendingTopicsThunk());
    }
  }, [trendingStatus, dispatch]);

  useEffect(() => {
    const ids = loadSavedPostIds();
    dispatch(hydrateSavedPosts({ ids }));
  }, [dispatch]);

  useEffect(() => {
    persistSavedPostIds(savedPostIds);
  }, [savedPostIds]);

  const handleBackToMain = () => {
  navigate('/app/chat');
  };

  const handleOpenNotifications = () => {
  navigate('/app/community/notifications');
  };

  const handleNavigateToTopic = useCallback(
    (topicId: string) => {
  navigate(`/app/community/topics/${topicId}`);
    },
    [navigate],
  );

  const handleOpenCreatorProfile = useCallback(
    (authorId: number) => {
  navigate(`/app/community/users/${authorId}`);
    },
    [navigate],
  );

  const userDisplayName = useMemo(() => {
    if (!user) {
      return '访客';
    }
    return user.name || user.username || '匿名用户';
  }, [user]);

  const recommendedCreators = useMemo<RecommendedCreator[]>(() => {
    if (!feedItems.length) {
      return [];
    }

    const currentUserId = user?.id ?? null;
    const authorStore = new Map<number, {
      id: number;
      name: string;
      username?: string | null;
      avatar_url?: string | null;
      posts: number;
      likes: number;
      comments: number;
      followsAuthor: boolean;
      topicCounter: Map<string, number>;
      latestTitle?: string;
      latestCreatedAt?: string | null;
    }>();

    feedItems.forEach((post) => {
      const authorId = post.author?.id;
      if (!authorId || authorId === currentUserId) {
        return;
      }
      if (!authorStore.has(authorId)) {
        authorStore.set(authorId, {
          id: authorId,
          name: post.author?.name || post.author?.username || '匿名用户',
          username: post.author?.username,
          avatar_url: post.author?.avatar_url,
          posts: 0,
          likes: 0,
          comments: 0,
          followsAuthor: Boolean(post.viewer?.follows_author),
          topicCounter: new Map(),
          latestTitle: post.title,
          latestCreatedAt: post.created_at ?? null,
        });
      }
      const entry = authorStore.get(authorId)!;
      entry.posts += 1;
      entry.likes += post.stats?.likes ?? 0;
      entry.comments += post.stats?.comments ?? 0;
      if (post.viewer?.follows_author) {
        entry.followsAuthor = true;
      }

      const topics = post.topic_details?.length
        ? post.topic_details.map((topic) => topic.label)
        : post.topics.map((topicId) => topicId);

      topics.forEach((label) => {
        entry.topicCounter.set(label, (entry.topicCounter.get(label) ?? 0) + 1);
      });

      if (!entry.latestCreatedAt || (post.created_at && post.created_at > entry.latestCreatedAt)) {
        entry.latestCreatedAt = post.created_at ?? entry.latestCreatedAt;
        entry.latestTitle = post.title;
      }
    });

    return Array.from(authorStore.values())
      .map((entry) => {
        const topTopics = Array.from(entry.topicCounter.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([label]) => label);
        const influenceScore = entry.likes * 2 + entry.comments + entry.posts * 3;
        return {
          id: entry.id,
          name: entry.name,
          username: entry.username,
          avatar_url: entry.avatar_url,
          posts: entry.posts,
          likes: entry.likes,
          comments: entry.comments,
          followsAuthor: entry.followsAuthor,
          topTopics,
          latestTitle: entry.latestTitle,
          latestCreatedAt: entry.latestCreatedAt,
          influenceScore,
        } satisfies RecommendedCreator & { influenceScore: number };
      })
      .sort((a, b) => {
        if (b.influenceScore === a.influenceScore) {
          return (b.latestCreatedAt ?? '').localeCompare(a.latestCreatedAt ?? '');
        }
        return b.influenceScore - a.influenceScore;
      })
      .slice(0, 4)
      .map(({ influenceScore, ...creator }) => creator);
  }, [feedItems, user?.id]);

  useNotificationStream();

  const handleToggleRecommendedFollow = async (authorId: number, isFollowing: boolean) => {
    if (followPendingMap[authorId]) {
      return;
    }
    setFollowPendingMap((prev) => ({ ...prev, [authorId]: true }));
    try {
      if (isFollowing) {
        await dispatch(unfollowCommunityAuthorThunk(authorId)).unwrap();
      } else {
        await dispatch(followCommunityAuthorThunk(authorId)).unwrap();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setFollowPendingMap((prev) => {
        const next = { ...prev };
        delete next[authorId];
        return next;
      });
    }
  };

  return (
    <div className="community-shell">
      <CommunityTopBar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onNotificationsClick={handleOpenNotifications}
        onBackToMain={handleBackToMain}
        userDisplayName={userDisplayName}
        avatarUrl={user?.avatar_url}
      />
      <div className="community-shell__body">
        <CommunityLeftNav />
        <main className="community-shell__main">
          <Outlet context={{ searchTerm: debouncedSearchTerm, searchInput, setSearchInput }} />
        </main>
        <CommunityRightAside
          trendingTopics={trendingTopics}
          trendingStatus={trendingStatus}
          trendingError={trendingError}
          onRefreshTrending={() => dispatch(fetchTrendingTopicsThunk())}
          onNavigateToTopic={handleNavigateToTopic}
          recommendedCreators={recommendedCreators}
          onToggleFollow={handleToggleRecommendedFollow}
          followPendingMap={followPendingMap}
          onOpenCreator={handleOpenCreatorProfile}
        />
      </div>
      <CommunityFloatingComposerButton />
    </div>
  );
};

export default CommunityLayout;
